// src/layouts/CustomAppBar.tsx
//
// "3c hybrid" app bar: 52px, white, hairline bottom border. Left: the
// mandatory two-tone brand wordmark (app-shell brand rule) plus a breadcrumb
// (group › section) resolved from the resource config. Right: environment
// pill (green in production, amber otherwise) and the language selector.
// v2 rule: the profile does NOT live here — it anchors the bottom of the
// sidebar rail (ProfileRailCard), a single place, as in roommatik.console.ui.
import React from 'react';
import { AppBar, LocalesMenuButton, useSidebarState, useTranslate } from 'react-admin';
import { alpha, Box, Button, IconButton, useMediaQuery, useTheme } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import SwitchAccountRoundedIcon from '@mui/icons-material/SwitchAccountRounded';
import { useLocation } from 'react-router-dom';
import { resourcesConfig } from '../config/resourceConfig';
import { SectionTabs } from './SectionTabs';
import { useSectionTabs } from './useSectionTabs';
import { getImpersonation, stopImpersonation } from '@/utils/impersonation';
import { AssistantButton } from '@/features/ask/components/AssistantButton';
import { AssistantConnectEntry } from '@/features/assistant-connect';

// Deployment environment, independent of the Vite build mode (every `vite build`
// runs with MODE=production regardless of the target server). Set VITE_APP_ENV
// at build time; defaults: production builds -> production, dev server -> test.
const IS_PRODUCTION =
    (import.meta.env.VITE_APP_ENV ?? (import.meta.env.PROD ? 'production' : 'test')) === 'production';

/** The breadcrumb's leaf for a page reached from inside a destination
 *  without being one of its tabs (e.g. /hotels under Administración). */
const findResourceLabel = (pathname: string): string | null => {
    const resource = Object.values(resourcesConfig).find(r =>
        r.route === '/' ? pathname === '/' : pathname === r.route || pathname.startsWith(`${r.route}/`)
    );
    return resource?.label ?? null;
};

export const CustomAppBar: React.FC = () => {
    const theme = useTheme();
    const translate = useTranslate();
    const location = useLocation();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [open, setOpen] = useSidebarState();

    // Breadcrumb = destination › active section, from the same resolution the
    // rail and the section bar read (hybrid-3c.md §5.1), so they never disagree.
    const sections = useSectionTabs();
    const leafKey = sections.tab?.labelKey ?? findResourceLabel(location.pathname);
    const groupLabelKey = sections.group && sections.group.labelKey !== leafKey ? sections.group.labelKey : null;
    const current = leafKey ? { groupLabelKey, nameKey: leafKey } : null;
    const impersonation = getImpersonation();

    return (
        <AppBar
            userMenu={false}
            toolbar={
                <>
                    {/* Impersonation pill — always-visible safety indicator (same slot
                        pattern as the environment pill). While it shows, every request
                        runs with the impersonated user's identity and permissions,
                        reads AND writes. */}
                    {impersonation && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                                height: '26px',
                                padding: '0 4px 0 10px',
                                borderRadius: theme.custom.radius.full,
                                backgroundColor: 'rgba(239,68,68,0.12)',
                                color: '#b91c1c',
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                minWidth: 0,
                            }}
                        >
                            <SwitchAccountRoundedIcon sx={{ fontSize: '16px', flexShrink: 0 }} />
                            <Box
                                component="span"
                                sx={{
                                    minWidth: 0,
                                    maxWidth: { xs: '110px', sm: '260px' },
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {translate('shell.impersonation.active', { name: impersonation.displayName })}
                            </Box>
                            <Button
                                size="small"
                                onClick={() => {
                                    stopImpersonation();
                                    window.location.hash = '#/';
                                    window.location.reload();
                                }}
                                sx={{
                                    minWidth: 0,
                                    height: '20px',
                                    padding: '0 8px',
                                    fontSize: '0.7rem',
                                    fontWeight: 700,
                                    color: '#b91c1c',
                                    flexShrink: 0,
                                }}
                            >
                                {translate('shell.impersonation.exit')}
                            </Button>
                        </Box>
                    )}
                    {/* Environment pill — only outside production (production needs no badge). */}
                    {!IS_PRODUCTION && (
                        <Box
                            sx={{
                                display: { xs: 'none', sm: 'flex' },
                                alignItems: 'center',
                                gap: 0.75,
                                height: '26px',
                                padding: '0 10px',
                                borderRadius: theme.custom.radius.full,
                                // v2 "pending/attention" tint from the warm accent token.
                                backgroundColor: alpha(theme.palette.warning.main, 0.16),
                                color: theme.palette.warning.dark,
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                whiteSpace: 'nowrap',
                            }}
                        >
                            <Box
                                component="span"
                                sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    backgroundColor: theme.palette.warning.main,
                                }}
                            />
                            TEST ENV
                        </Box>
                    )}
                    <AssistantButton />
                    <AssistantConnectEntry />
                    <LocalesMenuButton />
                </>
            }
            elevation={0}
            sx={{
                backgroundColor: (theme.palette.background as any).appbar || theme.palette.background.paper,
                color: theme.palette.text.primary,
                // 52px on every breakpoint (hybrid-3c.md §5 "App bar").
                height: '52px',
                minHeight: '52px',
                // Patrón A · escritorio: la barra va "encajada" SOBRE la columna de
                // contenido — absoluta, empezando en el borde derecho del rail (que
                // se desplaza cuando el rail se colapsa) y llegando hasta el borde
                // derecho del viewport. Móvil: fija a todo el ancho sobre el rail overlay.
                position: isMobile ? 'fixed' : 'absolute',
                top: 0,
                left: isMobile ? 0 : (open ? theme.custom.menu.openWidth : theme.custom.menu.closedWidth),
                right: 'auto',
                // Ancho explícito (no auto) para que el width:100% de MUI no gane y
                // empuje la barra fuera del borde derecho.
                width: isMobile
                    ? '100%'
                    : `calc(100% - ${open ? theme.custom.menu.openWidth : theme.custom.menu.closedWidth})`,
                zIndex: isMobile ? 1200 : theme.zIndex.appBar,
                transition: 'left 0.2s ease-in-out, width 0.2s ease-in-out',
                borderBottom: `1px solid ${theme.palette.divider}`,
                boxShadow: 'none',

                '& .MuiToolbar-root': {
                    padding: { xs: '0 8px', sm: '0 16px' },
                    display: 'flex',
                    width: '100%',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    height: '52px',
                    minHeight: '52px',
                    backgroundColor: (theme.palette.background as any).appbar || theme.palette.background.paper,
                    border: 'none',
                },
            }}
        >
            {/* Parte izquierda */}
            {!isMobile && (
                <Box sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    minWidth: 0,
                    gap: 1.5,
                }}>
                    <IconButton
                        color="inherit"
                        aria-label={translate('ra.action.menu')}
                        onClick={() => setOpen(!open)}
                        edge="start"
                        sx={{
                            mr: 2,
                            display: { md: 'none' },
                            width: '36px',
                            height: '36px',
                        }}
                    >
                        <MenuIcon />
                    </IconButton>

                    {/* v3 "1a": app bar carries ONLY the breadcrumb (Group › Section),
                        no wordmark and no leading chevron — the product name lives once,
                        on the rail. */}
                    {current && (
                        <Box
                            sx={{
                                display: { xs: 'none', md: 'flex' },
                                alignItems: 'center',
                                gap: 0.5,
                                minWidth: 0,
                                color: theme.custom.text.tertiary,
                                fontSize: '0.8125rem',
                            }}
                        >
                            {current.groupLabelKey && (
                                <>
                                    <Box component="span">{translate(current.groupLabelKey)}</Box>
                                    <ChevronRightRoundedIcon sx={{ fontSize: '16px' }} />
                                </>
                            )}
                            <Box component="span" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>
                                {translate(current.nameKey, { smart_count: 2 })}
                            </Box>
                        </Box>
                    )}
                </Box>
            )}
            <SectionTabs />
        </AppBar>
    );
};

export default CustomAppBar;
